<template>
  <div>
    <div class="products-details-section__grid grid gap-4">
      <div class="contents lg:block lg:[grid-area:header]">
        <div class="contents lg:flex lg:flex-wrap lg:justify-between lg:items-center">
          <ProductAverageReview
            class="[grid-area:average-review] max-lg:-mb-2 max-lg:-mt-4"
            :rating="product._rating ?? undefined"
            :show-schema="!!product._rating"
          />
          <ProductAttributeWrapper
            class="[grid-area:attributes] w-full lg:w-auto"
            :product="product"
          />
        </div>
        <ProductTitle
          class="[grid-area:title]"
          :product-title="productTitle"
        />
      </div>
      <div class="[grid-area:gallery]">
        <ProductGallery
          v-if="product"
          :product="product"
          :enable-zoom="true"
          :thumb-width="160"
          :thumb-height="100"
          :attributes-localized="attributesLocalized"
        />
      </div>
      <div class="[grid-area:pricing]">
        <ProductPricingSection
          :product="product"
          :attributes-localized="attributesLocalized"
          :product-adjusted-for-cart="productAdjustedForCart"
          :available-quantity="availableQuantity"
          :stock="stock"
          :loading-stocks="loadingStocks"
          :has-alternative-products="hasAlternativeProducts"
        />
      </div>
      <div class="[grid-area:details]">
        <ProductQuickDetails
          :product="product"
          :attributes-localized="attributesLocalized"
          :should-show-related-accessories="shouldShowRelatedAccessories"
        />
      </div>
      <div class="[grid-area:review]">
        <ProductTopReview :review="topReview" />
      </div>
    </div>
    <transition name="sf-collapse-bottom">
      <FloatingAddToCart
        v-if="showFloatingAddToCart"
        :product="product"
        :product-adjusted-for-cart="productAdjustedForCart"
      />
    </transition>
    <ProductDetailsMetaSchema
      :attributes-localized="attributesLocalized"
      :product="product"
    />
  </div>
</template>
<script lang="ts">
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import type { ProductProcessedAttributes } from '~~/types/product/attribute/ProductProcessedAttributes';
import type { ProductReview } from '~~/types/product/attribute/ProductReview';
import { productGetters } from '~/utils/ct';

export default defineComponent({
  name: 'ProductDetailsSection',
  props: {
    product: {
      type: Object as PropType<ProductVariantExtended>,
      required: true,
    },
    attributesLocalized: {
      type: Object as PropType<ProductProcessedAttributes>,
      required: true,
    },
    productAdjustedForCart: {
      type: Object,
      required: true,
    },
    availableQuantity: {
      type: Number,
      required: true,
    },
    stock: {
      type: Number,
      required: true,
    },
    loadingStocks: {
      type: Boolean,
      required: true,
    },
    topReview: {
      type: Object as PropType<ProductReview>,
      default: () => {

        return {};

      },
    },
    shouldShowRelatedAccessories: {
      type: Boolean,
      required: true,
    },
    hasAlternativeProducts: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {

    const { showFloatingAddToCart } = useFloatingATCSection();
    const productTitle = computed(() => productGetters.getName(props.product));

    return {
      showFloatingAddToCart,
      productTitle,
    };

  },
});
</script>

<style lang="scss" scoped>
.products-details-section {
  &__grid {
    grid-template-areas:
      "gallery"
      "title"
      "average-review"
      "pricing"
      "attributes"
      "details"
      "review";
    grid-template-columns: 100%;
  }

  @include for-desktop {
    &__grid {
      grid-template:
        "gallery header header" auto
        "gallery details pricing" auto
        "review details pricing" 1fr / 2fr 1fr 1fr;
    }
  }
}
</style>
